import { useState } from 'react';
import ChickenRabbitExplainer, {
  solveChickenRabbit,
} from '../components/explainers/ChickenRabbitExplainer';
import CatMouseExplainer, { solveCatMouse } from '../components/explainers/CatMouseExplainer';
import BackToMap from '../components/BackToMap';

/**
 * 图解数学演示页(/tujie)—— 家长辅导模式的原型
 *
 * 不走闯关流程、不连后端:家长选题型、填入作业里的数字,直接演示给孩子看。
 * 规划见 docs/plan-002-tujie-math-liuyi.md 2.4 节。
 *
 * 顶部只占一行:切换题型 + 当前题目的数字,点「换一题」才展开输入框 ——
 * 屏幕留给动画和讲解,整个过程在一屏手机里放得下。
 */

const TEMPLATES = [
  {
    id: 'chicken-rabbit',
    label: '🐔🐰 鸡兔同笼',
    Component: ChickenRabbitExplainer,
    defaults: { heads: 10, legs: 32 },
    fields: [
      { key: 'heads', label: '头' },
      { key: 'legs', label: '脚' },
    ],
    summary: (a) => `${a.heads} 个头 · ${a.legs} 只脚`,
    validate: (a) =>
      solveChickenRabbit(a.heads, a.legs)
        ? null
        : '脚数要是双数,并且在头数的 2 倍到 4 倍之间',
  },
  {
    id: 'cat-mouse',
    label: '🐱🐭 猫捉老鼠',
    Component: CatMouseExplainer,
    defaults: { catStart: 0, catStep: 3, mouseStart: 3, mouseStep: 2 },
    fields: [
      { key: 'catStart', label: '猫起点' },
      { key: 'catStep', label: '猫每次' },
      { key: 'mouseStart', label: '鼠起点' },
      { key: 'mouseStep', label: '鼠每次' },
    ],
    summary: (a) =>
      `猫从 ${a.catStart} 每次跳 ${a.catStep} · 鼠从 ${a.mouseStart} 每次跳 ${a.mouseStep}`,
    validate: (a) => solveCatMouse(a).error || null,
  },
];

function toInputs(args) {
  return Object.fromEntries(Object.entries(args).map(([k, v]) => [k, String(v)]));
}

function TujiePage() {
  const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
  const template = TEMPLATES.find((t) => t.id === templateId);
  // 每个题型各记各的数字,来回切换不丢
  const [argsById, setArgsById] = useState(
    Object.fromEntries(TEMPLATES.map((t) => [t.id, t.defaults])),
  );
  const shown = argsById[templateId];

  const [editing, setEditing] = useState(false);
  const [inputs, setInputs] = useState(toInputs(shown));
  const draft = Object.fromEntries(Object.entries(inputs).map(([k, v]) => [k, Number(v)]));
  const error = editing ? template.validate(draft) : null;

  const switchTemplate = (id) => {
    setTemplateId(id);
    setEditing(false);
    setInputs(toInputs(argsById[id]));
  };

  const { Component } = template;

  return (
    <div className="min-h-screen bg-qiji-paper px-3 py-3">
      <div className="mx-auto flex max-w-md flex-col gap-3">
        <BackToMap />
        <header className="flex flex-col gap-2 rounded-xl border-2 border-black bg-white px-3 py-2 shadow-brutal-sm">
          <nav className="flex gap-1.5" aria-label="题型">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={t.id === templateId}
                onClick={() => switchTemplate(t.id)}
                className={`rounded-md border-2 border-black px-2 py-0.5 text-sm font-bold ${
                  t.id === templateId ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>

          {!editing ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-gray-700">{template.summary(shown)}</span>
              <button
                type="button"
                onClick={() => {
                  setInputs(toInputs(shown));
                  setEditing(true);
                }}
                className="shrink-0 rounded-md border-2 border-black bg-qiji-gold px-2 py-0.5 text-sm font-bold"
              >
                换一题
              </button>
            </div>
          ) : (
            <form
              className="flex flex-wrap items-center gap-x-2 gap-y-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                if (error) return;
                setArgsById({ ...argsById, [templateId]: draft });
                setEditing(false);
              }}
            >
              {template.fields.map((f) => (
                <label key={f.key} className="flex items-center gap-1 text-sm">
                  {f.label}
                  <input
                    type="number"
                    min="0"
                    value={inputs[f.key]}
                    onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.value })}
                    className="w-12 rounded-md border-2 border-black px-1 py-0.5 text-center text-sm"
                  />
                </label>
              ))}
              <button
                type="submit"
                disabled={Boolean(error)}
                className="ml-auto rounded-md border-2 border-black bg-qiji-gold px-2 py-0.5 text-sm font-bold disabled:opacity-40"
              >
                好
              </button>
              {error && <p className="w-full text-xs text-qiji-coral">{error}</p>}
            </form>
          )}
        </header>

        <div className="rounded-2xl border-2 border-black bg-white p-3 shadow-brutal">
          {/* key 变化时整个动画重置到第 0 步 */}
          <Component key={`${templateId}-${JSON.stringify(shown)}`} {...shown} />
        </div>
      </div>
    </div>
  );
}

export default TujiePage;
